import React from "react";
import {
  AbsoluteFill,
  Audio,
  staticFile,
  useCurrentFrame,
} from "remotion";
import { Sparkles, TrendingUp, ShieldCheck, Flame } from "lucide-react";
import { TechBackground } from "./vfx/TechBackground";
import { MotionCard } from "./vfx/MotionCard";
import { KineticSubtitle, SubtitleCue } from "./vfx/KineticSubtitle";
import { ImpactShake } from "./vfx/ImpactShake";

export const MotionVfxShowcase: React.FC = () => {
  const frame = useCurrentFrame();

  // Color Palette (Cyber Emerald & Warm Gold)
  const emeraldAccent = "#10B981";
  const goldAccent = "#F59E0B";

  // Subtitle timing mapped to demo_mastered_ardi.mp3 (7.8s / 234 frames)
  const SUBTITLE_CUES: SubtitleCue[] = [
    {
      from: 0,
      to: 75,
      text: "Ini bukan sekadar animasi biasa.",
      highlightWords: ["bukan", "biasa"],
    },
    {
      from: 75,
      to: 155,
      text: "Ini adalah standar baru motion graphics,",
      highlightWords: ["standar", "baru", "motion"],
    },
    {
      from: 155,
      to: 234,
      text: "dengan audio broadcast jernih dan visual dinamis.",
      highlightWords: ["broadcast", "jernih", "dinamis"],
    },
  ];

  // Impact keyframes for micro-camera shake
  const IMPACT_FRAMES = [0, 75, 155];

  return (
    <ImpactShake impactFrames={IMPACT_FRAMES} intensity={7}>
      <AbsoluteFill
        style={{
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Inter', sans-serif",
          color: "#FFFFFF",
          overflow: "hidden",
        }}
      >
        {/* 1. VFX Layer: Animated Mesh & Dotted Background */}
        <TechBackground theme="dark-emerald" accentColor={emeraldAccent} />

        {/* 2. Audio Layer: Mastered Voiceover + Micro SFX */}
        <Audio src={staticFile("demo_mastered_ardi.mp3")} volume={1.0} />
        {frame === 0 && <Audio src={staticFile("whoosh-short.mp3")} volume={0.4} />}
        {frame === 75 && <Audio src={staticFile("click.mp3")} volume={0.6} />}
        {frame === 155 && <Audio src={staticFile("chime.mp3")} volume={0.8} />}

        {/* 3. Header Branding Safe Zone (Y: 260px - Safe from TikTok Top UI) */}
        <div
          style={{
            position: "absolute",
            top: 280,
            left: 0,
            right: 0,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 12,
            zIndex: 10,
          }}
        >
          <div
            style={{
              padding: "8px 20px",
              borderRadius: 30,
              background: "rgba(16, 185, 129, 0.12)",
              border: "1px solid rgba(16, 185, 129, 0.35)",
              backdropFilter: "blur(12px)",
              display: "flex",
              alignItems: "center",
              gap: 8,
              boxShadow: "0 0 20px rgba(16, 185, 129, 0.2)",
            }}
          >
            <Sparkles size={18} color={emeraldAccent} />
            <span
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: emeraldAccent,
                letterSpacing: "1.5px",
                textTransform: "uppercase",
              }}
            >
              Next-Gen Motion v2.3
            </span>
          </div>
        </div>

        {/* 4. Scene 1: Kinetic Visual Hero (Frame 0 - 80) */}
        {frame < 85 && (
          <AbsoluteFill
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "0 60px",
              marginBottom: 100,
            }}
          >
            <MotionCard delay={5} width={760}>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.02) 100%)",
                  padding: "48px 40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 20,
                }}
              >
                <div
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 24,
                    background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 12px 30px rgba(16, 185, 129, 0.4)",
                  }}
                >
                  <Flame size={44} color="#FFFFFF" />
                </div>

                <div
                  style={{
                    fontSize: 48,
                    fontWeight: 900,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.15,
                  }}
                >
                  Smooth Motion.
                  <br />
                  <span style={{ color: emeraldAccent }}>Zero Stutter.</span>
                </div>

                <div
                  style={{
                    fontSize: 24,
                    color: "#E5E7EB",
                    fontWeight: 500,
                    lineHeight: 1.5,
                  }}
                >
                  Physics springs & 3D card perspective rendered seamlessly in code.
                </div>
              </div>
            </MotionCard>
          </AbsoluteFill>
        )}

        {/* 5. Scene 2: Tech Performance Metrics (Frame 85 - 160) */}
        {frame >= 85 && frame < 165 && (
          <AbsoluteFill
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "0 60px",
              marginBottom: 100,
            }}
          >
            <MotionCard delay={88} width={760}>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(10, 20, 14, 0.8) 100%)",
                  border: "1px solid rgba(16, 185, 129, 0.3)",
                  padding: "40px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 24,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <TrendingUp size={24} color={goldAccent} />
                    <span style={{ fontSize: 20, fontWeight: 800, color: goldAccent }}>
                      PERFORMANCE STATS
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: 14,
                      padding: "4px 12px",
                      borderRadius: 12,
                      background: "rgba(245, 158, 11, 0.2)",
                      color: goldAccent,
                      fontWeight: 700,
                    }}
                  >
                    60 FPS READY
                  </span>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <div
                    style={{
                      padding: 24,
                      borderRadius: 18,
                      background: "rgba(255, 255, 255, 0.04)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                    }}
                  >
                    <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.5)", textTransform: "uppercase" }}>
                      Audio Fidelity
                    </div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: emeraldAccent, marginTop: 4 }}>
                      -16 LUFS
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.7)" }}>
                      Broadcast Clean
                    </div>
                  </div>

                  <div
                    style={{
                      padding: 24,
                      borderRadius: 18,
                      background: "rgba(255, 255, 255, 0.04)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                    }}
                  >
                    <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.5)", textTransform: "uppercase" }}>
                      Animation Easing
                    </div>
                    <div style={{ fontSize: 36, fontWeight: 900, color: "#FFFFFF", marginTop: 4 }}>
                      Spring 160
                    </div>
                    <div style={{ fontSize: 13, color: "rgba(255, 255, 255, 0.7)" }}>
                      Natural Damping
                    </div>
                  </div>
                </div>
              </div>
            </MotionCard>
          </AbsoluteFill>
        )}

        {/* 6. Scene 3: Studio Sheen Finale (Frame 165 - 234) */}
        {frame >= 165 && (
          <AbsoluteFill
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              padding: "0 60px",
              marginBottom: 100,
            }}
          >
            <MotionCard delay={168} width={760}>
              <div
                style={{
                  background:
                    "linear-gradient(135deg, rgba(245, 158, 11, 0.15) 0%, rgba(16, 185, 129, 0.15) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  padding: "48px 40px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  gap: 20,
                }}
              >
                <div
                  style={{
                    width: 76,
                    height: 76,
                    borderRadius: "50%",
                    background: "rgba(255, 255, 255, 0.1)",
                    border: "2px solid #FFFFFF",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: "0 0 35px rgba(255, 255, 255, 0.3)",
                  }}
                >
                  <ShieldCheck size={42} color="#FFFFFF" />
                </div>

                <div
                  style={{
                    fontSize: 44,
                    fontWeight: 900,
                    letterSpacing: "-0.03em",
                    lineHeight: 1.15,
                  }}
                >
                  Studio-Grade Polish.
                  <br />
                  <span style={{ color: goldAccent }}>Ready to Dominate.</span>
                </div>
              </div>
            </MotionCard>
          </AbsoluteFill>
        )}

        {/* 7. Word-by-Word Kinetic Subtitle Overlay (Strict Safe Zone bottom: 520px) */}
        <KineticSubtitle
          cues={SUBTITLE_CUES}
          accentColor={emeraldAccent}
          fontSize={42}
          bottomPosition={520}
        />
      </AbsoluteFill>
    </ImpactShake>
  );
};
